export default class ViewerWatermark {
    constructor(viewer, watermarkUrl, position, dispPages) {
        /**
         * ウォーターマークを描画するビューア
         * @private
         * @type {OpenSeadragon.viewer} 
         */
        this._viewer = viewer;

        /**
         * デバイスのピクセルレシオ。
         * @type {Number}
         */
        this._pixelRatio = window.devicePixelRatio > 1 ? window.devicePixelRatio : 1;

        /**
         * ウォーターマークを描画する位置。
         * @type {Number}
         */
        this._position = position;

        /**
         * ウォーターマークを描画するページ定義 
         * @type {Array<Number>}
         */
        this._dispPages = dispPages;

        /**
         * ウォーターマークを描画イベントハンドラ。 
         * @type {function}
         */
        this._drawWatermarkHandler = this._drawWatermark.bind(this);

        /**
         * ウォーターマーク画像。 
         * @type {Image}
         */
        this._watermark = new Image();
        this._watermark.onload = function() {
            this._viewer.addHandler('update-viewport', this._drawWatermarkHandler);
            this._wmWidth = this._watermark.width * this._pixelRatio;
            this._wmHeight = this._watermark.height * this._pixelRatio;
        }.bind(this);
        this._watermark.src = watermarkUrl;
    }

    /**
        * ウォーターマークを描画する。
        * @function IRIArchives._drawWatermark
        * @private
        */
    _drawWatermark() {

        var currentPage = parseInt(this._viewer.currentPage()) + 1;

        // 現在のページが表示するページリストに含まれていない場合は抜ける。
        if (!this.canDrawWatermark()) return;

        // 画像のサイズを取得する。
        var imageSize = this._viewer.source.dimensions;

        // 回転角を取得する。
        var rotate = this._viewer.viewport.getRotation();

        // 画像の貼り付け領域を取得する。
        var imageCorners = this._detectCorners(imageSize);

        // ウォーターマークのサイズを計算する。
        var wmSize = this._detectWMSize(imageCorners, rotate);

        // ウォーターマークサイズから貼り付け位置を補正する。
        imageCorners.positionCorrection(wmSize);

        // ウォーターマークの貼り付け位置を取得する。
        var point = this._calcWmCoordinates(imageCorners, wmSize, rotate);

        // canvasにウオーターマーク画像を張り付ける。
        this._viewer.drawer.context.drawImage(this._watermark, point.x, point.y, wmSize.x, wmSize.y);
    }

    /**
     * ウォーターマーク貼り付け可能領域を判定する。
     * @function IRIArchives._detectCorners
     * @private
     * @param {OpenSeadragon.Point} imageSize 画像のサイズ(pixel)。
     * @return {IRIArchives.RectCorners} 貼り付け可能領域。
     */
    _detectCorners(imageSize) {
        // ビューアにおける画像の四隅の座標を取得する。
        var topLeft = this._viewer.world.getItemAt(0).imageToViewerElementCoordinates(new OpenSeadragon.Point(0, 0)).times(this._pixelRatio);
        var topRight = this._viewer.world.getItemAt(0).imageToViewerElementCoordinates(new OpenSeadragon.Point(imageSize.x, 0)).times(this._pixelRatio);
        var bottomLeft = this._viewer.world.getItemAt(0).imageToViewerElementCoordinates(new OpenSeadragon.Point(0, imageSize.y)).times(this._pixelRatio);
        var bottomRight = this._viewer.world.getItemAt(0).imageToViewerElementCoordinates(imageSize).times(this._pixelRatio);

        var viewerElem = this._viewer.element;
        return new ViewerWatermarkRect(topLeft, topRight, bottomRight, bottomLeft, 0, viewerElem.clientWidth * this._pixelRatio, 0, viewerElem.clientHeight * this._pixelRatio);
    }

    /**
     * ウォーターマークのサイズを求める。
     * @function IRIArchives._detectWMSize
     * @private
     * @param {IRIArchives.RectCorners} imageCorners 画像のサイズ(pixel)。
     * @param {Number} degrees 回転角(degree)。
     * @return {OpenSeadragon.Point} ウォーターマークのサイズ。
     */
    _detectWMSize(imageCorners, degrees) {
        // ウォーターマーク表示可能サイズを求める。
        var dispRegionWidth = imageCorners.getWidth(degrees);
        var dispRegionHeight = imageCorners.getHeight(degrees);

        // 表示可能領域がビューアの端にかかっていない場合は表示可能領域を0.8倍にする。
        if (imageCorners.getLeft(degrees) !== 0 && imageCorners.getRight(degrees) !== this._viewer.element.clientWidth * this._pixelRatio) dispRegionWidth = dispRegionWidth * 0.8;
        if (imageCorners.getTop(degrees) !== 0 && imageCorners.getBottom(degrees) !== this._viewer.element.clientHeight * this._pixelRatio) dispRegionHeight = dispRegionHeight * 0.8;

        // ウォーターマークの大きさ。
        var wmSize = new OpenSeadragon.Point(this._wmWidth, this._wmHeight);
        var wmAspectRatio = this._wmWidth / this._wmHeight;

        // ウォーターマークの大きさが表示可能領域より小さい場合は縮小を行う。
        if (this._wmWidth > dispRegionWidth) {
            wmSize.x = dispRegionWidth;
            wmSize.y = wmSize.x / wmAspectRatio;
        }

        if (wmSize.y > dispRegionHeight) {
            wmSize.y = dispRegionHeight;
            wmSize.x = wmSize.y * wmAspectRatio;
        }

        return wmSize;
    }

    /**
     * ウォーターマークの座標を求める。
     * @function IRIArchives._calcWmCoordinates
     * @private
     * @param {WatermarkRect} imageCorners 画像のサイズ(pixel)。
     * @param {OpenSeadragon.Point} wmSize ウォーターマークのサイズ。
     * @param {Number} degrees 回転角(degree)。
     * @return {OpenSeadragon.Point} ウォーターマークの座標。
     */
    _calcWmCoordinates(imageCorners, wmSize, degrees) {

        var coordinates = new OpenSeadragon.Point();

        //ウォーターマークの貼り付け座標は左上の座標が基準なので、
        //ウォーターマークの貼り付け位置が右もしくは下に位置している場合はウォーターマークのサイズを考慮する。
        switch (this._position) {
            case positionDef.TopLeft:
                coordinates.x = imageCorners.getTopLeft(degrees).x;
                coordinates.y = imageCorners.getTopLeft(degrees).y;
                break;

            case positionDef.TopRight:
                coordinates.x = imageCorners.getTopRight(degrees).x - wmSize.x;
                coordinates.y = imageCorners.getTopRight(degrees).y;
                break;

            case positionDef.BottomRight:
                coordinates.x = imageCorners.getBottomRight(degrees).x - wmSize.x;
                coordinates.y = imageCorners.getBottomRight(degrees).y - wmSize.y;
                break;

            case positionDef.BottomLeft:
                coordinates.x = imageCorners.getBottomLeft(degrees).x;
                coordinates.y = imageCorners.getBottomLeft(degrees).y - wmSize.y;
                break;
        }

        if (coordinates.x < 0) coordinates.x = 0;
        if (coordinates.x + wmSize.x > this._viewer.element.clientWidth * this._pixelRatio) coordinates.x = this._viewer.element.clientWidth * this._pixelRatio - wmSize.x;

        if (coordinates.y < 0) coordinates.y = 0;
        if (coordinates.y + wmSize.y > this._viewer.element.clientHeight * this._pixelRatio) coordinates.y = this._viewer.element.clientHeight * this._pixelRatio - wmSize.y;

        return coordinates;
    }


    /**
     * ウォーターマークが描写できるか判定する。
     * @returns {Boolean} ウォーターマークの可否。
     */
    canDrawWatermark() {

        if (!this._dispPages) return true;

        var currentPage = parseInt(this._viewer.currentPage()) + 1;
        return this._dispPages.indexOf(currentPage) !== -1;
    }

    /**
     * ウォーターマークを破棄します。
     */
    destroy() {

        this._viewer.removeHandler('update-viewport', this._drawWatermarkHandler);

        this._viewer = null;
        this._pixelRatio = null;

        this._position = null;
        this._dispPages = null;

        this._watermark = null;

        this._wmWidth = null;
        this._wmHeight = null;

        this._drawWatermarkHandler = null;
    }
}


class ViewerWatermarkRect {
    constructor(topLeft, topRight, bottomRight, bottomLeft, minX, maxX, minY, maxY) {
        this.topLeft = this._normalize(topLeft, minX, maxX, minY, maxY);
        this.topRight = this._normalize(topRight, minX, maxX, minY, maxY);
        this.bottomRight = this._normalize(bottomRight, minX, maxX, minY, maxY);
        this.bottomLeft = this._normalize(bottomLeft, minX, maxX, minY, maxY);

        this._pixelRatio = window.devicePixelRatio;
    }

    /**
         * 幅を取得する。
         * @function IRIArchives.RectCorners.prototype.getWidth
         * @param {Number} degrees 現在の角度(degree)。
         * @returns {Number} 幅(pixel)。
         */
    getWidth(degrees) {
        if (degrees >= 0 && degrees < 90) return this.topRight.x - this.bottomLeft.x;
        if (degrees >= 90 && degrees < 180) return this.topLeft.x - this.bottomRight.x;
        if (degrees >= 180 && degrees < 270) return this.bottomLeft.x - this.topRight.x;
        if (degrees >= 270 && degrees < 360) return this.bottomRight.x - this.topLeft.x;

        return this.getWidth(degrees - 360);
    }

    /**
     * 高さを取得する。
     * @function IRIArchives.RectCorners.prototype.getHeight
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {Number} 高さ(pixel)。
     */
    getHeight(degrees) {
        if (degrees >= 0 && degrees < 90) return this.bottomRight.y - this.topLeft.y;
        if (degrees >= 90 && degrees < 180) return this.topRight.y - this.bottomLeft.y;
        if (degrees >= 180 && degrees < 270) return this.topLeft.y - this.bottomRight.y;
        if (degrees >= 270 && degrees < 360) return this.bottomLeft.y - this.topRight.y;

        return this.getHeight(degrees - 360);
    }

    /**
     * 上底のY座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getTop
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {Number} 上底のY座標(pixel)。
     */
    getTop(degrees) {
        if (degrees >= 0 && degrees < 90) return this.topLeft.y;
        if (degrees >= 90 && degrees < 180) return this.bottomLeft.y;
        if (degrees >= 180 && degrees < 270) return this.bottomRight.y;
        if (degrees >= 270 && degrees < 360) return this.topRight.y;

        return this.getTop(degrees - 360);
    }

    /**
     * 右端のX座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getRight
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {Number} 右端のX座標(pixel)。
     */
    getRight(degrees) {
        if (degrees >= 0 && degrees < 90) return this.topRight.x;
        if (degrees >= 90 && degrees < 180) return this.topLeft.x;
        if (degrees >= 180 && degrees < 270) return this.bottomLeft.x;
        if (degrees >= 270 && degrees < 360) return this.bottomRight.x;

        return this.getRight(degrees - 360);
    }

    /**
     * 下底のY座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getBottom
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {Number} 下底のY座標(pixel)。
     */
    getBottom(degrees) {
        if (degrees >= 0 && degrees < 90) return this.bottomRight.y;
        if (degrees >= 90 && degrees < 180) return this.topRight.y;
        if (degrees >= 180 && degrees < 270) return this.topLeft.y;
        if (degrees >= 270 && degrees < 360) return this.bottomLeft.y;

        return this.getTop(degrees - 360);
    }

    /**
     * 左端のX座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getLeft
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {Number} 左端のX座標(pixel)。
     */
    getLeft(degrees) {
        if (degrees >= 0 && degrees < 90) return this.bottomLeft.x;
        if (degrees >= 90 && degrees < 180) return this.bottomRight.x;
        if (degrees >= 180 && degrees < 270) return this.topRight.x;
        if (degrees >= 270 && degrees < 360) return this.topLeft.x;

        return this.getLeft(degrees - 360);
    }

    /**
     * 回転後の左上の頂点の座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getTopLeft
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {OpenSeadragon.Point} 頂点の座標。
     */
    getTopLeft(degrees) {
        if (degrees >= 45 && degrees < 135) return this.bottomLeft;
        if (degrees >= 135 && degrees < 225) return this.bottomRight;
        if (degrees >= 225 && degrees < 315) return this.topRight;

        return this.topLeft;
    }

    /**
     * 回転後の左下の頂点の座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getBottomLeft
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {OpenSeadragon.Point} 頂点の座標。
     */
    getBottomLeft(degrees) {
        if (degrees >= 45 && degrees < 135) return this.bottomRight;
        if (degrees >= 135 && degrees < 225) return this.topRight;
        if (degrees >= 225 && degrees < 315) return this.topLeft;

        return this.bottomLeft;
    }

    /**
     * 回転後の右下の頂点の座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getBottomRight
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {OpenSeadragon.Point} 頂点の座標。
     */
    getBottomRight(degrees) {
        if (degrees >= 45 && degrees < 135) return this.topRight;
        if (degrees >= 135 && degrees < 225) return this.topLeft;
        if (degrees >= 225 && degrees < 315) return this.bottomLeft;

        return this.bottomRight;
    }

    /**
     * 回転後の右上の頂点の座標を取得する。
     * @function IRIArchives.RectCorners.prototype.getTopRigth
     * @param {Number} degrees 現在の角度(degree)。
     * @returns {OpenSeadragon.Point} 頂点の座標。
     */
    getTopRight(degrees) {
        if (degrees >= 45 && degrees < 135) return this.topLeft;
        if (degrees >= 135 && degrees < 225) return this.bottomLeft;
        if (degrees >= 225 && degrees < 315) return this.bottomRight;

        return this.topRight;
    }

    /**
     * 位置の補正を行う。
     * @function IRIArchives.RectCorners.prototype.positionCorrection
     * @param {OpenSeadragon.Point} correctionValue 補正値。
     */
    positionCorrection(correctionValue) {
        var correctedX = this.maxX;
        var correctedY = this.maxY;

        this.topLeft = this._normalize(this.topLeft, 0, correctedX, 0, correctedY);
        this.topRight = this._normalize(this.topRight, 0, correctedX, 0, correctedY);
        this.bottomRight = this._normalize(this.bottomRight, 0, correctedX, 0, correctedY);
        this.bottomLeft = this._normalize(this.bottomLeft, 0, correctedX, 0, correctedY);
    }

    /**
     * 座標を補正して、閾値を超えないようにする。
     * @private
     * @function IRIArchives.RectCorners.prototype._normalize
     * @param {OpenSeadragon.Point} corner 補正する座標。
     * @param {Number} minX X座標の最小値。
     * @param {Number} maxX X座標の最大値。
     * @param {Number} minY Y座標の最小値。
     * @param {Number} maxY Y座標の最大値。
     * @returns {OpenSeadragon.Point} 補正後の値。
     */
    _normalize(corner, minX, maxX, minY, maxY) {

        if (corner.x < minX) {
            corner.x = minX;
        }
        else if (corner.x > maxX) {
            corner.x = maxX;
        }

        if (corner.y < minY) {
            corner.y = minY;
        }
        else if (corner.y > maxY) {
            corner.y = maxY;
        }

        return corner;
    }
}

const positionDef = {
    /** @member {Number} */
    /** 左上 */
    TopLeft: 0,
    /** @member {Number} */
    /** 右上 */
    TopRight: 1,
    /** @member {Number} */
    /** 右下 */
    BottomRight: 2,
    /** @member {Number} */
    /** 左下 */
    BottomLeft: 3
};
